/* eslint-disable no-lonely-if */
import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";

import Dialog, { DialogTitle, DialogContent, DialogActions } from "components/common/Dialog";
import CustomButton from "components/common/Button";
import TimeRangeSelector from "components/CheckInCheckOut/TimeRangeSelector";
import EndDayModal from "components/CheckInCheckOut/EndDayModal/EndDayModal";
import TimesheetResultModal from "../TimesheetResultModal/TimesheetResultModal";
import PageLoader from "components/common/PageLoader";

import { getTimesheetDetailsById, updateTimeSheetById } from "services/CheckInCheckOut/TimesheetGetAndUpdate";
import { hasPermission } from "hooks/usePermission";

import "./TimesheetDetailModal.scss";

const getNextDayLabelInfo = (startTime, endTime, baseDate = null, scheduleStart = null, scheduleEnd = null) => {
  if (!endTime) {
    return { showLabel: false, text: "" };
  }

  if (!startTime) {
    const options = { day: "numeric", month: "short", year: "numeric" };
    const displayDate = baseDate || endTime;
    return { showLabel: true, text: displayDate.toLocaleDateString("en-US", options) };
  }

  // First check if startTime and endTime already have actual calendar dates set
  // This happens when data comes from API with full datetime strings
  // We check if the dates match baseDate to distinguish between API data and picker data
  if (baseDate) {
    const startDateOnly = new Date(startTime);
    startDateOnly.setHours(0, 0, 0, 0);
    const endDateOnly = new Date(endTime);
    endDateOnly.setHours(0, 0, 0, 0);
    const baseDateOnly = new Date(baseDate);
    baseDateOnly.setHours(0, 0, 0, 0);

    // Check if startTime's date matches baseDate or is one day after baseDate
    // This indicates API data with correct dates (not today's date from picker)
    const startMatchesBase = startDateOnly.getTime() === baseDateOnly.getTime();
    const startIsNextDay = startDateOnly.getTime() === baseDateOnly.getTime() + 86400000;

    if (startMatchesBase || startIsNextDay) {
      // This is API data with correct dates, use the actual calendar dates
      if (startDateOnly.getTime() === endDateOnly.getTime()) {
        // Both on same date
        const options = { day: "numeric", month: "short", year: "numeric" };
        return { showLabel: true, text: endTime.toLocaleDateString("en-US", options) };
      }

      // If endTime is a day after startTime, show "Next Day"
      const dayDifference = (endDateOnly.getTime() - startDateOnly.getTime()) / (1000 * 60 * 60 * 24);
      if (dayDifference === 1) {
        return { showLabel: true, text: "Next Day" };
      }
    }
  }

  if (!baseDate || !scheduleStart || !scheduleEnd) {
    // Fallback to original logic if we don't have schedule info
    const startMinutes = startTime.getHours() * 60 + startTime.getMinutes();
    const endMinutes = endTime.getHours() * 60 + endTime.getMinutes();

    if (endMinutes < startMinutes) {
      return { showLabel: true, text: "Next Day" };
    }

    const options = { day: "numeric", month: "short", year: "numeric" };
    const displayDate = baseDate || endTime;
    return { showLabel: true, text: displayDate.toLocaleDateString("en-US", options) };
  }

  // Helper to convert time to minutes
  const toMinutes = (t) => {
    if (typeof t === "string") {
      const [h, m] = t.split(":").map(Number);
      return h * 60 + m;
    }
    return t.getHours() * 60 + t.getMinutes();
  };

  const startMinutes = toMinutes(startTime);
  const endMinutes = toMinutes(endTime);
  const scheduleStartMinutes = toMinutes(scheduleStart);
  const scheduleEndMinutes = toMinutes(scheduleEnd);

  // Determine if this is an overnight shift
  const isOvernightShift = scheduleEndMinutes < scheduleStartMinutes;

  // Determine what date the clock-out belongs to
  const clockOutDate = new Date(baseDate);

  if (isOvernightShift) {
    // For overnight shifts, if clock-out time is before schedule start, it's next day
    if (endMinutes < scheduleStartMinutes) {
      clockOutDate.setDate(clockOutDate.getDate() + 1);
    }
  } else {
    // For regular shifts, if end time is before start time, it's next day
    if (endMinutes < startMinutes) {
      clockOutDate.setDate(clockOutDate.getDate() + 1);
    }
  }

  // Format the date for display
  const options = { day: "numeric", month: "short", year: "numeric" };
  const formattedDate = clockOutDate.toLocaleDateString("en-US", options);

  // Check if clock-out is on a different day than the base date
  const baseDateOnly = new Date(baseDate);
  baseDateOnly.setHours(0, 0, 0, 0);
  const clockOutDateOnly = new Date(clockOutDate);
  clockOutDateOnly.setHours(0, 0, 0, 0);

  // Check if this is actually an overnight scenario (clock-out before clock-in)
  const isOvernightClockInOut = endMinutes < startMinutes;

  // Check if clock-in is also on the "next day" (early morning hours)
  // If clock-in is before schedule start (for overnight shifts), it means clock-in is already on next day
  const isClockInAlreadyNextDay = isOvernightShift && startMinutes < scheduleStartMinutes;

  if (isOvernightClockInOut) {
    // Clock-out is before clock-in (overnight scenario)
    if (isClockInAlreadyNextDay) {
      // Both clock-in and clock-out are on the next day (e.g., 12:01 AM to 1:03 AM)
      return { showLabel: true, text: formattedDate };
    }
    // Clock-in is on base day, clock-out is next day (e.g., 2 PM to 12:01 AM)
    return { showLabel: true, text: "Next Day" };
  }
  // Same day scenario (e.g., 2 PM to 11 PM)
  return { showLabel: true, text: formattedDate };
};

function TimesheetDetailModal({
  open,
  onClose,
  rowData = null,
  isEditable = true,
  attendanceDetails = null,
  employeeId = null,
  onUpdateSuccess = null,
  employeeDetails = {},
}) {
  const loggedInUser = JSON.parse(localStorage.getItem("loggedInUser"));
  const isEdgeAdmin = loggedInUser?.type === "edge";
  const canAdjustTimesheet = hasPermission("timeTracking.adjust");
  const canEdit = (isEdgeAdmin || isEditable) && canAdjustTimesheet;
  const { firstName, lastName } = employeeDetails;
  const employeeName = `${firstName} ${lastName}` || loggedInUser?.name || "";

  const [loading, setLoading] = useState(false);

  const [clockInTime, setClockInTime] = useState(null);
  const [clockOutTime, setClockOutTime] = useState(null);
  const [clockOutDate, setClockOutDate] = useState(null);
  const [originalClockInDate, setOriginalClockInDate] = useState(null);

  const [breakSlots, setBreakSlots] = useState([{ id: 1, startTime: null, endTime: null, date: null }]);

  const [nursingSlots, setNursingSlots] = useState([{ id: 1, startTime: null, endTime: null, date: null }]);

  // Store initial values for reset on cancel
  const [initialClockInTime, setInitialClockInTime] = useState(null);
  const [initialClockOutTime, setInitialClockOutTime] = useState(null);
  const [initialClockOutDate, setInitialClockOutDate] = useState(null);
  const [initialBreakSlots, setInitialBreakSlots] = useState([{ id: 1, startTime: null, endTime: null, date: null }]);
  const [initialNursingSlots, setInitialNursingSlots] = useState([
    { id: 1, startTime: null, endTime: null, date: null },
  ]);

  const [showEndDayModal, setShowEndDayModal] = useState(false);

  const [showResultModal, setShowResultModal] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [saving, setSaving] = useState(false);

  // Real-time calculated summary values
  const [calculatedTimeWorked, setCalculatedTimeWorked] = useState("0 hr 0 min");
  const [calculatedBreakDuration, setCalculatedBreakDuration] = useState("0 min");
  const [calculatedNursingDuration, setCalculatedNursingDuration] = useState("0 min");

  // Validation error states
  const [startTimeError, setStartTimeError] = useState("");
  const [endTimeError, setEndTimeError] = useState("");
  const [breakSlotsError, setBreakSlotsError] = useState("");
  const [nursingBreakError, setNursingBreakError] = useState("");

  const formatShiftTime = (startTime, endTime) => {
    if (!startTime || !endTime) return "N/A";

    try {
      const today = new Date().toISOString().split("T")[0];
      const startDateTime = new Date(`${today}T${startTime}`);
      const endDateTime = new Date(`${today}T${endTime}`);

      const formatTimeWithTimezone = (date) => {
        const hours = date.getHours();
        const minutes = date.getMinutes();
        const ampm = hours >= 12 ? "PM" : "AM";
        const displayHour = hours % 12 || 12;
        const displayMinutes = minutes.toString().padStart(2, "0");
        return `${displayHour}:${displayMinutes} ${ampm}`;
      };

      return `${formatTimeWithTimezone(startDateTime)} - ${formatTimeWithTimezone(endDateTime)}`;
    } catch (error) {
      return "N/A";
    }
  };

  const parseDateTime = (dateTimeString) => {
    if (!dateTimeString) return null;
    return new Date(dateTimeString);
  };

  const calculateDuration = (startTime, endTime) => {
    if (!startTime || !endTime) return "";

    // Calculate based on time of day, not date objects
    const startMinutes = startTime.getHours() * 60 + startTime.getMinutes();
    const endMinutes = endTime.getHours() * 60 + endTime.getMinutes();

    let totalMinutes;
    if (endMinutes < startMinutes) {
      // Overnight shift
      totalMinutes = 24 * 60 - startMinutes + endMinutes;
    } else {
      // Same day
      totalMinutes = endMinutes - startMinutes;
    }

    const hours = Math.floor(totalMinutes / 60);
    const minutes = Math.floor(totalMinutes % 60);
    return `${hours} hr ${minutes} min`;
  };

  const calculateTotalBreakDuration = (slots) => {
    let totalMinutes = 0;
    slots.forEach((slot) => {
      if (slot.startTime && slot.endTime) {
        const startMinutes = slot.startTime.getHours() * 60 + slot.startTime.getMinutes();
        const endMinutes = slot.endTime.getHours() * 60 + slot.endTime.getMinutes();

        let duration;
        if (endMinutes < startMinutes) {
          // Overnight break
          duration = 24 * 60 - startMinutes + endMinutes;
        } else {
          // Same day break
          duration = endMinutes - startMinutes;
        }

        totalMinutes += duration;
      }
    });
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    if (hours > 0) {
      return `${hours} hr ${minutes} min`;
    }
    return `${minutes} min`;
  };

  // Validation helper functions
  const parseShiftTime = (timeString) => {
    if (!timeString) return null;
    const today = new Date().toISOString().split("T")[0];
    return new Date(`${today}T${timeString}`);
  };

  const validateClockInTime = useCallback(() => {
    if (!clockInTime || !attendanceDetails?.schedule?.startTime) {
      setStartTimeError("");
      return true;
    }

    const shiftStartTime = parseShiftTime(attendanceDetails.schedule.startTime);
    const shiftEndTime = attendanceDetails?.schedule?.endTime
      ? parseShiftTime(attendanceDetails.schedule.endTime)
      : null;

    if (!shiftStartTime) {
      setStartTimeError("");
      return true;
    }

    // Extract time portion only for comparison
    const clockInMinutes = clockInTime.getHours() * 60 + clockInTime.getMinutes();
    const shiftStartMinutes = shiftStartTime.getHours() * 60 + shiftStartTime.getMinutes();

    // Calculate early clock-in threshold (3 hours before shift start, capped at midnight)
    const threeHoursInMinutes = 3 * 60; // 180 minutes
    const earlyClockInMinutes = Math.max(0, shiftStartMinutes - threeHoursInMinutes);

    // If we have shift end time, check if clock-in falls within the shift window
    if (shiftEndTime) {
      const shiftEndMinutes = shiftEndTime.getHours() * 60 + shiftEndTime.getMinutes();

      // Check if this is an overnight shift
      const isOvernightShift = shiftEndMinutes < shiftStartMinutes;

      if (isOvernightShift) {
        // For overnight shifts, clock-in is valid if:
        // 1. It's at or after early clock-in time (3 hours before shift start), OR
        // 2. It's before or at shift end (next day)
        const isValidClockIn = clockInMinutes >= earlyClockInMinutes || clockInMinutes <= shiftEndMinutes;

        if (!isValidClockIn) {
          setStartTimeError("Clock in time should be within 3 hours before shift start time");
          return false;
        }
      } else {
        // For regular shifts, clock-in should be between early clock-in time and shift end
        if (clockInMinutes < earlyClockInMinutes || clockInMinutes > shiftEndMinutes) {
          setStartTimeError("Clock in time should be within 3 hours before shift start time");
          return false;
        }
      }
    } else {
      // If no shift end time, just check against early clock-in time
      if (clockInMinutes < earlyClockInMinutes) {
        setStartTimeError("Clock in time should be within 3 hours before shift start time");
        return false;
      }
    }

    setStartTimeError("");
    return true;
  }, [clockInTime, attendanceDetails?.schedule?.startTime, attendanceDetails?.schedule?.endTime]);

  const validateClockOutTime = useCallback(() => {
    if (!clockOutTime) {
      setEndTimeError("");
      return true;
    }

    // Validate clock-out is not more than 3 hours after shift end time
    if (attendanceDetails?.schedule?.endTime) {
      const shiftEndTime = parseShiftTime(attendanceDetails.schedule.endTime);
      if (shiftEndTime && clockInTime) {
        // Get time in minutes for easier comparison
        const clockInMinutes = clockInTime.getHours() * 60 + clockInTime.getMinutes();
        const clockOutMinutes = clockOutTime.getHours() * 60 + clockOutTime.getMinutes();
        const shiftEndMinutes = shiftEndTime.getHours() * 60 + shiftEndTime.getMinutes();

        // Normalize clock-out time (handle overnight)
        let normalizedClockOutMinutes = clockOutMinutes;
        if (clockOutMinutes < clockInMinutes) {
          // Clock-out is next day
          normalizedClockOutMinutes += 24 * 60;
        }

        // Normalize shift end time (handle overnight shifts)
        let normalizedShiftEndMinutes = shiftEndMinutes;
        if (shiftEndMinutes < clockInMinutes) {
          // Shift ends next day
          normalizedShiftEndMinutes += 24 * 60;
        }

        // Calculate max allowed clock-out (shift end + 3 hours)
        const maxAllowedMinutes = normalizedShiftEndMinutes + 3 * 60;

        if (normalizedClockOutMinutes > maxAllowedMinutes) {
          setEndTimeError("Clock-out time cannot exceed 3 hours beyond the shift end time.");
          return false;
        }
      }
    }

    setEndTimeError("");
    return true;
  }, [clockOutTime, clockInTime, attendanceDetails?.schedule?.endTime]);

  const validateBreakSlots = useCallback(() => {
    // Validate nursing breaks duration
    let totalNursingMinutes = 0;
    nursingSlots.forEach((slot) => {
      if (slot.startTime && slot.endTime) {
        const startMin = slot.startTime.getHours() * 60 + slot.startTime.getMinutes();
        const endMin = slot.endTime.getHours() * 60 + slot.endTime.getMinutes();

        let duration;
        if (endMin < startMin) {
          // Overnight nursing break
          duration = 24 * 60 - startMin + endMin;
        } else {
          // Same day nursing break
          duration = endMin - startMin;
        }

        totalNursingMinutes += duration;
      }
    });

    if (totalNursingMinutes > 60) {
      setNursingBreakError("Your break shouldn't exceed 1 hour. Adjust the break time to proceed.");
    } else {
      setNursingBreakError("");
    }

    // Get shift start and end times for comparison
    const shiftStartTime = attendanceDetails?.schedule?.startTime
      ? parseShiftTime(attendanceDetails.schedule.startTime)
      : null;
    const shiftEndTime = attendanceDetails?.schedule?.endTime
      ? parseShiftTime(attendanceDetails.schedule.endTime)
      : null;

    // If we don't have shift times, we can't validate against them
    if (!shiftStartTime || !shiftEndTime) {
      setBreakSlotsError("");
      return totalNursingMinutes <= 60;
    }

    // Extract time components for comparison
    const shiftStartMinutes = shiftStartTime.getHours() * 60 + shiftStartTime.getMinutes();
    let shiftEndMinutes = shiftEndTime.getHours() * 60 + shiftEndTime.getMinutes();

    // Calculate early clock-in threshold (3 hours before shift start, capped at midnight)
    const threeHoursInMinutes = 3 * 60; // 180 minutes
    const earlyClockInMinutes = Math.max(0, shiftStartMinutes - threeHoursInMinutes);

    // Handle overnight shifts (e.g., 9 PM to 5 AM)
    if (shiftEndMinutes < shiftStartMinutes) {
      shiftEndMinutes += 24 * 60; // Add 24 hours to end time
    }

    // Check if regular breaks are outside shift hours
    let hasRegularBreakOutsideShift = false;
    for (let i = 0; i < breakSlots.length; i += 1) {
      const slot = breakSlots[i];
      if (slot.startTime && slot.endTime) {
        let breakStartMinutes = slot.startTime.getHours() * 60 + slot.startTime.getMinutes();
        let breakEndMinutes = slot.endTime.getHours() * 60 + slot.endTime.getMinutes();

        // Handle overnight breaks
        if (breakEndMinutes < breakStartMinutes) {
          breakEndMinutes += 24 * 60;
        }

        // For overnight shifts, adjust break times if they appear to be on the next day
        if (shiftEndMinutes > 24 * 60) {
          if (breakStartMinutes < earlyClockInMinutes) {
            breakStartMinutes += 24 * 60;
          }
          if (breakEndMinutes < earlyClockInMinutes) {
            breakEndMinutes += 24 * 60;
          }
        }

        // Check if break is outside shift boundaries (allowing 3 hours early)
        if (breakStartMinutes < earlyClockInMinutes || breakEndMinutes > shiftEndMinutes) {
          hasRegularBreakOutsideShift = true;
          break;
        }
      }
    }

    // Check if nursing breaks are outside shift hours
    let hasNursingBreakOutsideShift = false;
    for (let i = 0; i < nursingSlots.length; i += 1) {
      const slot = nursingSlots[i];
      if (slot.startTime && slot.endTime) {
        let breakStartMinutes = slot.startTime.getHours() * 60 + slot.startTime.getMinutes();
        let breakEndMinutes = slot.endTime.getHours() * 60 + slot.endTime.getMinutes();

        // Handle overnight breaks
        if (breakEndMinutes < breakStartMinutes) {
          breakEndMinutes += 24 * 60;
        }

        // For overnight shifts, adjust break times if they appear to be on the next day
        if (shiftEndMinutes > 24 * 60) {
          if (breakStartMinutes < earlyClockInMinutes) {
            breakStartMinutes += 24 * 60;
          }
          if (breakEndMinutes < earlyClockInMinutes) {
            breakEndMinutes += 24 * 60;
          }
        }

        // Check if break is outside shift boundaries (allowing 3 hours early)
        if (breakStartMinutes < earlyClockInMinutes || breakEndMinutes > shiftEndMinutes) {
          hasNursingBreakOutsideShift = true;
          break;
        }
      }
    }

    // Set errors separately for regular breaks and nursing breaks
    if (hasRegularBreakOutsideShift) {
      setBreakSlotsError("Break hours cannot be outside the shift hours");
    } else {
      setBreakSlotsError("");
    }

    // Combine nursing break errors (duration and outside shift)
    if (hasNursingBreakOutsideShift && totalNursingMinutes > 60) {
      setNursingBreakError("Break hours cannot be outside the shift hours and shouldn't exceed 1 hour.");
    } else if (hasNursingBreakOutsideShift) {
      setNursingBreakError("Break hours cannot be outside the shift hours");
    } else if (totalNursingMinutes > 60) {
      setNursingBreakError("Your break shouldn't exceed 1 hour. Adjust the break time to proceed.");
    } else {
      setNursingBreakError("");
    }

    return !hasRegularBreakOutsideShift && !hasNursingBreakOutsideShift && totalNursingMinutes <= 60;
  }, [breakSlots, nursingSlots, attendanceDetails?.schedule?.startTime, attendanceDetails?.schedule?.endTime]);

  // Run validations whenever relevant fields change
  useEffect(() => {
    validateClockInTime();
  }, [validateClockInTime]);

  useEffect(() => {
    validateClockOutTime();
  }, [validateClockOutTime]);

  useEffect(() => {
    validateBreakSlots();
  }, [validateBreakSlots]);

  // Fetch timesheet details when modal opens
  useEffect(() => {
    const fetchTimesheetDetails = async () => {
      if (!open) {
        return;
      }

      // For missed shifts (no timesheetId), reset all fields and use the date from rowData
      if (!rowData?.timesheetId) {
        // Reset all fields to empty for missed shifts
        setClockInTime(null);
        setClockOutTime(null);
        setClockOutDate(null);
        setBreakSlots([{ id: 1, startTime: null, endTime: null, date: null }]);
        setNursingSlots([{ id: 1, startTime: null, endTime: null, date: null }]);

        // Set initial values to null for missed shifts
        setInitialClockInTime(null);
        setInitialClockOutTime(null);
        setInitialClockOutDate(null);
        setInitialBreakSlots([{ id: 1, startTime: null, endTime: null, date: null }]);
        setInitialNursingSlots([{ id: 1, startTime: null, endTime: null, date: null }]);

        // Clear validation errors
        setStartTimeError("");
        setEndTimeError("");
        setBreakSlotsError("");
        setNursingBreakError("");

        if (rowData?.rawDate) {
          const missedShiftDate = new Date(rowData.rawDate);
          setOriginalClockInDate(missedShiftDate);
        }
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await getTimesheetDetailsById(rowData.timesheetId);

        if (response?.success && response?.data) {
          let clockIn = null;
          let clockOut = null;
          let clockOutDateValue = null;

          if (response.data.attendance) {
            const { attendance } = response.data;
            clockIn = parseDateTime(attendance.startTime);
            clockOut = parseDateTime(attendance.endTime);

            // Store the shift date (not clock-in date) as the base date for calculations
            // attendance.date is the shift date (e.g., Oct 16), even if clock-in is on Oct 17
            const shiftDate = new Date(attendance.date);
            setOriginalClockInDate(shiftDate);

            const startDate = new Date(attendance.startTime);
            const endDate = new Date(attendance.endTime);
            if (endDate.getDate() !== startDate.getDate()) {
              clockOutDateValue = endDate;
            }

            setClockInTime(clockIn);
            setClockOutTime(clockOut);
            setClockOutDate(clockOutDateValue);

            // Save initial values
            setInitialClockInTime(clockIn);
            setInitialClockOutTime(clockOut);
            setInitialClockOutDate(clockOutDateValue);
          }

          let breaks = [{ id: 1, startTime: null, endTime: null, date: null }];
          if (response.data.breaks && response.data.breaks.length > 0) {
            const mappedBreaks = response.data.breaks.map((breakItem, index) => {
              const startTime = parseDateTime(breakItem.startTime);
              const endTime = parseDateTime(breakItem.endTime);
              const nextDayInfo = getNextDayLabelInfo(startTime, endTime);

              return {
                id: index + 1,
                startTime,
                endTime,
                date: parseDateTime(breakItem.endTime),
                showNextDayLabel: nextDayInfo.showLabel,
                nextDayText: nextDayInfo.text,
              };
            });
            breaks = mappedBreaks;
          }
          setBreakSlots(breaks);
          // Deep copy with proper date handling
          setInitialBreakSlots(
            breaks.map((slot) => ({
              ...slot,
              startTime: slot.startTime ? new Date(slot.startTime) : null,
              endTime: slot.endTime ? new Date(slot.endTime) : null,
              date: slot.date ? new Date(slot.date) : null,
            }))
          );

          let nursing = [{ id: 1, startTime: null, endTime: null, date: null }];
          if (response.data.nursingBreaks && response.data.nursingBreaks.length > 0) {
            const mappedNursing = response.data.nursingBreaks.map((nursingItem, index) => {
              const startTime = parseDateTime(nursingItem.startTime);
              const endTime = parseDateTime(nursingItem.endTime);
              const nextDayInfo = getNextDayLabelInfo(startTime, endTime);

              return {
                id: index + 1,
                startTime,
                endTime,
                date: parseDateTime(nursingItem.endTime),
                showNextDayLabel: nextDayInfo.showLabel,
                nextDayText: nextDayInfo.text,
              };
            });
            nursing = mappedNursing;
          }
          setNursingSlots(nursing);
          // Deep copy with proper date handling
          setInitialNursingSlots(
            nursing.map((slot) => ({
              ...slot,
              startTime: slot.startTime ? new Date(slot.startTime) : null,
              endTime: slot.endTime ? new Date(slot.endTime) : null,
              date: slot.date ? new Date(slot.date) : null,
            }))
          );
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Error fetching timesheet details:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchTimesheetDetails();
  }, [open, rowData?.timesheetId, rowData?.rawDate]);

  const handleAddBreakSlot = () => {
    const newSlot = {
      id: breakSlots.length + 1,
      startTime: null,
      endTime: null,
      date: null,
    };
    setBreakSlots([...breakSlots, newSlot]);
  };

  const handleRemoveBreakSlot = (slotId) => {
    if (breakSlots.length > 1) {
      setBreakSlots(breakSlots.filter((slot) => slot.id !== slotId));
    }
  };

  const handleBreakSlotChange = (slotId, field, value) => {
    setBreakSlots(
      breakSlots.map((slot) => {
        if (slot.id === slotId) {
          const updatedSlot = { ...slot, [field]: value };

          // Recalculate next day info if both times are present
          if (updatedSlot.startTime && updatedSlot.endTime) {
            const nextDayInfo = getNextDayLabelInfo(
              updatedSlot.startTime,
              updatedSlot.endTime,
              originalClockInDate,
              attendanceDetails?.schedule?.startTime,
              attendanceDetails?.schedule?.endTime
            );
            updatedSlot.showNextDayLabel = nextDayInfo.showLabel;
            updatedSlot.nextDayText = nextDayInfo.text;
          }

          return updatedSlot;
        }
        return slot;
      })
    );
  };

  const handleAddNursingSlot = () => {
    const newSlot = {
      id: nursingSlots.length + 1,
      startTime: null,
      endTime: null,
      date: null,
    };
    setNursingSlots([...nursingSlots, newSlot]);
  };

  const handleRemoveNursingSlot = (slotId) => {
    if (nursingSlots.length > 1) {
      setNursingSlots(nursingSlots.filter((slot) => slot.id !== slotId));
    }
  };

  const handleNursingSlotChange = (slotId, field, value) => {
    setNursingSlots(
      nursingSlots.map((slot) => {
        if (slot.id === slotId) {
          const updatedSlot = { ...slot, [field]: value };

          // Recalculate next day info if both times are present
          if (updatedSlot.startTime && updatedSlot.endTime) {
            const nextDayInfo = getNextDayLabelInfo(
              updatedSlot.startTime,
              updatedSlot.endTime,
              originalClockInDate,
              attendanceDetails?.schedule?.startTime,
              attendanceDetails?.schedule?.endTime
            );
            updatedSlot.showNextDayLabel = nextDayInfo.showLabel;
            updatedSlot.nextDayText = nextDayInfo.text;
          }

          return updatedSlot;
        }
        return slot;
      })
    );
  };

  // Real-time calculation of summary values whenever times change
  useEffect(() => {
    // Calculate time worked (clock in to clock out minus breaks and nursing)
    if (clockInTime && clockOutTime) {
      // Normalize both times to use the same base date for calculation
      const clockInMinutes = clockInTime.getHours() * 60 + clockInTime.getMinutes();
      const clockOutMinutes = clockOutTime.getHours() * 60 + clockOutTime.getMinutes();

      let totalMinutes;
      if (clockOutMinutes < clockInMinutes) {
        // Overnight shift - clock out is next day
        totalMinutes = 24 * 60 - clockInMinutes + clockOutMinutes;
      } else {
        // Same day shift
        totalMinutes = clockOutMinutes - clockInMinutes;
      }

      // Calculate total break time
      let breakMinutes = 0;
      breakSlots.forEach((slot) => {
        if (slot.startTime && slot.endTime) {
          const startMin = slot.startTime.getHours() * 60 + slot.startTime.getMinutes();
          const endMin = slot.endTime.getHours() * 60 + slot.endTime.getMinutes();

          let duration;
          if (endMin < startMin) {
            // Overnight break
            duration = 24 * 60 - startMin + endMin;
          } else {
            // Same day break
            duration = endMin - startMin;
          }

          breakMinutes += duration;
        }
      });

      // Calculate total nursing break time
      let nursingMinutes = 0;
      nursingSlots.forEach((slot) => {
        if (slot.startTime && slot.endTime) {
          const startMin = slot.startTime.getHours() * 60 + slot.startTime.getMinutes();
          const endMin = slot.endTime.getHours() * 60 + slot.endTime.getMinutes();

          let duration;
          if (endMin < startMin) {
            // Overnight nursing break
            duration = 24 * 60 - startMin + endMin;
          } else {
            // Same day nursing break
            duration = endMin - startMin;
          }

          nursingMinutes += duration;
        }
      });

      // Time worked = total time - breaks - nursing breaks
      const workedMinutes = totalMinutes - breakMinutes - nursingMinutes;
      if (workedMinutes < 0) {
        setCalculatedTimeWorked("-");
      } else {
        const hours = Math.floor(workedMinutes / 60);
        const minutes = Math.floor(workedMinutes % 60);
        setCalculatedTimeWorked(hours > 0 ? `${hours} hr ${minutes} min` : `${minutes} min`);
      }

      // Set break duration
      if (breakMinutes < 0) {
        setCalculatedBreakDuration("-");
      } else {
        const breakHours = Math.floor(breakMinutes / 60);
        const breakMins = Math.floor(breakMinutes % 60);
        setCalculatedBreakDuration(breakHours > 0 ? `${breakHours} hr ${breakMins} min` : `${breakMins} min`);
      }

      // Set nursing duration
      if (nursingMinutes < 0) {
        setCalculatedNursingDuration("-");
      } else {
        const nursingHours = Math.floor(nursingMinutes / 60);
        const nursingMins = Math.floor(nursingMinutes % 60);
        setCalculatedNursingDuration(nursingHours > 0 ? `${nursingHours} hr ${nursingMins} min` : `${nursingMins} min`);
      }
    } else {
      // If no clock in/out times, just calculate break totals
      let breakMinutes = 0;
      breakSlots.forEach((slot) => {
        if (slot.startTime && slot.endTime) {
          const startMin = slot.startTime.getHours() * 60 + slot.startTime.getMinutes();
          const endMin = slot.endTime.getHours() * 60 + slot.endTime.getMinutes();

          let duration;
          if (endMin < startMin) {
            // Overnight break
            duration = 24 * 60 - startMin + endMin;
          } else {
            // Same day break
            duration = endMin - startMin;
          }

          breakMinutes += duration;
        }
      });

      let nursingMinutes = 0;
      nursingSlots.forEach((slot) => {
        if (slot.startTime && slot.endTime) {
          const startMin = slot.startTime.getHours() * 60 + slot.startTime.getMinutes();
          const endMin = slot.endTime.getHours() * 60 + slot.endTime.getMinutes();

          let duration;
          if (endMin < startMin) {
            // Overnight nursing break
            duration = 24 * 60 - startMin + endMin;
          } else {
            // Same day nursing break
            duration = endMin - startMin;
          }

          nursingMinutes += duration;
        }
      });

      if (breakMinutes < 0) {
        setCalculatedBreakDuration("-");
      } else {
        const breakHours = Math.floor(breakMinutes / 60);
        const breakMins = Math.floor(breakMinutes % 60);
        setCalculatedBreakDuration(breakHours > 0 ? `${breakHours} hr ${breakMins} min` : `${breakMins} min`);
      }

      if (nursingMinutes < 0) {
        setCalculatedNursingDuration("-");
      } else {
        const nursingHours = Math.floor(nursingMinutes / 60);
        const nursingMins = Math.floor(nursingMinutes % 60);
        setCalculatedNursingDuration(nursingHours > 0 ? `${nursingHours} hr ${nursingMins} min` : `${nursingMins} min`);
      }

      setCalculatedTimeWorked("-");
    }
  }, [clockInTime, clockOutTime, breakSlots, nursingSlots]);

  const handleSave = () => {
    setShowEndDayModal(true);
  };

  const handleClose = () => {
    // Restore initial values on cancel
    resetFields();
    onClose();
  };

  const handleEndDayClose = () => {
    setShowEndDayModal(false);
  };

  const getDateForSelectedTime = (baseDate, scheduleStart, scheduleEnd, selectedTime) => {
    const toMinutes = (t) => {
      if (typeof t === "string") {
        const [h, m] = t.split(":").map(Number);
        return h * 60 + m;
      }
      // If it's a Date object
      return t.getHours() * 60 + t.getMinutes();
    };

    const start = toMinutes(scheduleStart);
    const end = toMinutes(scheduleEnd);
    const selected = toMinutes(selectedTime);

    const result = new Date(baseDate);
    const isOvernight = end < start;

    if (isOvernight) {
      // For overnight shifts (e.g., 6 PM - 2 AM):
      // Base date is the shift start date (e.g., Nov 24)
      // Times that should be on NEXT day (Nov 25):
      //   - Between midnight (0) and shift end (e.g., 0-120 for 2 AM)
      // Times that should stay on BASE day (Nov 24):
      //   - Everything else (e.g., 3 PM - 11:59 PM including early clock-in window)
      if (selected <= end) {
        // Time is between midnight and shift end (e.g., 12 AM - 2 AM) → next day
        result.setDate(result.getDate() + 1);
      }
      // else: Time is after shift end → keep it on base date
    }

    return {
      date: result,
      formattedDate: result.toISOString().split("T")[0],
    };
  };

  const formatDateTimeForPayload = (dateTime, useOriginalDate = false) => {
    if (!dateTime) return null;

    let targetDate;

    if (
      useOriginalDate &&
      originalClockInDate &&
      attendanceDetails?.schedule?.startTime &&
      attendanceDetails?.schedule?.endTime
    ) {
      // Use the overnight shift logic to determine the correct date
      const selectedTimeString = `${String(dateTime.getHours()).padStart(2, "0")}:${String(dateTime.getMinutes()).padStart(2, "0")}`;
      const dateInfo = getDateForSelectedTime(
        originalClockInDate,
        attendanceDetails.schedule.startTime,
        attendanceDetails.schedule.endTime,
        selectedTimeString
      );

      targetDate = dateInfo.date;
      targetDate.setHours(dateTime.getHours());
      targetDate.setMinutes(dateTime.getMinutes());
      targetDate.setSeconds(0);
      targetDate.setMilliseconds(0);
    } else {
      targetDate = new Date(dateTime);
    }

    const year = targetDate.getFullYear();
    const month = String(targetDate.getMonth() + 1).padStart(2, "0");
    const day = String(targetDate.getDate()).padStart(2, "0");
    const hours = String(targetDate.getHours()).padStart(2, "0");
    const minutes = String(targetDate.getMinutes()).padStart(2, "0");
    return `${year}-${month}-${day} ${hours}:${minutes}`;
  };

  const resetFields = () => {
    // Reset all fields to initial values
    setClockInTime(initialClockInTime);
    setClockOutTime(initialClockOutTime);
    setClockOutDate(initialClockOutDate);

    // Deep copy to avoid reference issues - recreate Date objects
    if (initialBreakSlots && initialBreakSlots.length > 0) {
      setBreakSlots(
        initialBreakSlots.map((slot) => ({
          ...slot,
          startTime: slot.startTime ? new Date(slot.startTime) : null,
          endTime: slot.endTime ? new Date(slot.endTime) : null,
          date: slot.date ? new Date(slot.date) : null,
        }))
      );
    } else {
      setBreakSlots([{ id: 1, startTime: null, endTime: null, date: null }]);
    }

    if (initialNursingSlots && initialNursingSlots.length > 0) {
      setNursingSlots(
        initialNursingSlots.map((slot) => ({
          ...slot,
          startTime: slot.startTime ? new Date(slot.startTime) : null,
          endTime: slot.endTime ? new Date(slot.endTime) : null,
          date: slot.date ? new Date(slot.date) : null,
        }))
      );
    } else {
      setNursingSlots([{ id: 1, startTime: null, endTime: null, date: null }]);
    }

    // Clear validation errors
    setStartTimeError("");
    setEndTimeError("");
    setBreakSlotsError("");
    setNursingBreakError("");
  };

  const handleEndDayConfirm = async () => {
    try {
      setSaving(true);

      const payload = {
        employeeId,
        attendance: {
          startTime: formatDateTimeForPayload(clockInTime, true, false, null),
          endTime: formatDateTimeForPayload(clockOutTime, true, true, clockInTime),
        },
        breaks: breakSlots
          .filter((slot) => slot.startTime && slot.endTime)
          .map((slot) => ({
            startTime: formatDateTimeForPayload(slot.startTime, true, false, null),
            endTime: formatDateTimeForPayload(slot.endTime, true, true, slot.startTime),
          })),
        nursingBreaks: nursingSlots
          .filter((slot) => slot.startTime && slot.endTime)
          .map((slot) => ({
            startTime: formatDateTimeForPayload(slot.startTime, true, false, null),
            endTime: formatDateTimeForPayload(slot.endTime, true, true, slot.startTime),
          })),
      };

      const response = await updateTimeSheetById(rowData.timesheetId, payload);
      setSaving(false);
      setShowEndDayModal(false);

      if (response?.payload?.success) {
        setIsSuccess(true);
        if (onUpdateSuccess) {
          onUpdateSuccess();
        }
      } else {
        setIsSuccess(false);
      }

      // Reset fields after API call completes (success or failure)
      resetFields();
      setShowResultModal(true);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error saving timesheet:", error);
      setSaving(false);
      setShowEndDayModal(false);
      setIsSuccess(false);
      // Reset fields after API call fails
      resetFields();
      setShowResultModal(true);
    }
  };

  const handleResultModalClose = () => {
    setShowResultModal(false);
    onClose();
  };

  const shiftTime = attendanceDetails?.schedule
    ? formatShiftTime(attendanceDetails.schedule.startTime, attendanceDetails.schedule.endTime)
    : "N/A";

  const timeWorked = calculatedTimeWorked;
  const breakDuration = calculatedBreakDuration;
  const timeSpentNursing = calculatedNursingDuration;

  const daySummary = {
    totalHours: calculateDuration(clockInTime, clockOutTime) || "0 hr 0 min",
    timeWorked: calculatedTimeWorked,
    breaks: calculatedBreakDuration,
    nursing: calculatedNursingDuration,
  };

  const getDialogActions = () => {
    if (loading) {
      return null;
    }

    if (!canEdit) {
      return (
        <DialogActions
          action2={<CustomButton label="Done" variant="contained" color="primary" onClick={handleClose} />}
        />
      );
    }

    return (
      <DialogActions
        action1={<CustomButton label="Cancel" variant="outlined" color="secondary" onClick={handleClose} />}
        action2={
          <CustomButton
            label="Save"
            size="medium"
            variant="contained"
            color="primary"
            onClick={handleSave}
            isDisabled={!!startTimeError || !!endTimeError || !!breakSlotsError || !!nursingBreakError}
          />
        }
      />
    );
  };

  return (
    <>
      <Dialog
        open={open && !showEndDayModal && !showResultModal}
        onClose={handleClose}
        size="md"
        title={<DialogTitle title={rowData?.date || "Wednesday, Jan 15, 2025"} />}
        content={
          <DialogContent>
            {loading ? (
              <PageLoader />
            ) : (
              <div className="timesheet-detail-content">
                {/* Summary Cards */}
                <div className="timesheet-summary-grid">
                  <div className="summary-card">
                    <p className="summary-label">Shift time (local time)</p>
                    <div className="summary-value">{shiftTime}</div>
                  </div>
                  <div className="summary-card">
                    <p className="summary-label">Time worked</p>
                    <div className="summary-value">{timeWorked}</div>
                  </div>
                  <div className="summary-card">
                    <p className="summary-label">Break duration</p>
                    <div className="summary-value">{breakDuration}</div>
                  </div>
                  <div className="summary-card">
                    <p className="summary-label">Time spent nursing</p>
                    <div className="summary-value">{timeSpentNursing}</div>
                  </div>
                </div>

                {/* Clock in & Clock out */}
                <TimeRangeSelector
                  startTime={clockInTime}
                  endTime={clockOutTime}
                  onStartTimeChange={setClockInTime}
                  onEndTimeChange={setClockOutTime}
                  startLabel="Clock in"
                  endLabel="Clock out"
                  helperText=""
                  showArrow
                  dateValue={clockOutDate}
                  onDateChange={setClockOutDate}
                  dateLabel="Date"
                  disabled={!canEdit}
                  showHeading
                  heading="Clock in & Clock out"
                  totalDuration={calculateDuration(clockInTime, clockOutTime)}
                  showNextDayLabel={
                    getNextDayLabelInfo(
                      clockInTime,
                      clockOutTime,
                      originalClockInDate,
                      attendanceDetails?.schedule?.startTime,
                      attendanceDetails?.schedule?.endTime
                    ).showLabel
                  }
                  nextDayText={
                    getNextDayLabelInfo(
                      clockInTime,
                      clockOutTime,
                      originalClockInDate,
                      attendanceDetails?.schedule?.startTime,
                      attendanceDetails?.schedule?.endTime
                    ).text
                  }
                  nextDayLabel="Date"
                  startTimeError={startTimeError}
                  endTimeError={endTimeError}
                />

                {/* Break time */}
                <TimeRangeSelector
                  multipleSlots
                  slots={breakSlots}
                  onSlotChange={handleBreakSlotChange}
                  onAddSlot={handleAddBreakSlot}
                  onRemoveSlot={handleRemoveBreakSlot}
                  startLabel="Start time"
                  endLabel="End time"
                  helperText=""
                  showArrow
                  dateLabel="Date"
                  disabled={!canEdit}
                  showHeading
                  heading="Break time"
                  totalDuration={calculateTotalBreakDuration(breakSlots)}
                  addSlotButtonText="Add break slot"
                  nextDayLabel="Date"
                  breakSlotsError={breakSlotsError}
                />

                {/* Nursing break */}
                <TimeRangeSelector
                  multipleSlots
                  slots={nursingSlots}
                  onSlotChange={handleNursingSlotChange}
                  onAddSlot={handleAddNursingSlot}
                  onRemoveSlot={handleRemoveNursingSlot}
                  startLabel="Start time"
                  endLabel="End time"
                  helperText=""
                  showArrow
                  dateLabel="Date"
                  disabled={!canEdit}
                  showHeading
                  heading="Nursing break"
                  totalDuration={`${calculateTotalBreakDuration(nursingSlots)} (1 hr max)`}
                  addSlotButtonText="Add nursing slot"
                  nextDayLabel="Date"
                  breakSlotsError={nursingBreakError}
                />
              </div>
            )}
          </DialogContent>
        }
        actions={getDialogActions()}
      />

      <EndDayModal
        open={showEndDayModal}
        onClose={handleEndDayClose}
        onConfirm={handleEndDayConfirm}
        daySummary={daySummary}
        title="Are you sure you want to save changes to this timesheet?"
        employeeName={employeeName}
        date={rowData?.date || "Jan 15, 2025"}
        description=""
        cancelLabel="Cancel"
        confirmLabel="Yes, Save"
        cancelVariant="outlined"
        confirmVariant="contained"
        confirmColor="primary"
        summaryTitle=""
        isLoading={saving}
      />

      <TimesheetResultModal open={showResultModal} onClose={handleResultModalClose} isSuccess={isSuccess} />
    </>
  );
}

TimesheetDetailModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  rowData: PropTypes.shape({
    id: PropTypes.number,
    timesheetId: PropTypes.number,
    date: PropTypes.string,
    rawDate: PropTypes.string,
    status: PropTypes.string,
    clockedIn: PropTypes.string,
    clockedOut: PropTypes.string,
    timeWorked: PropTypes.string,
    timeWorkedSeconds: PropTypes.number,
    breakDuration: PropTypes.string,
    breakSeconds: PropTypes.number,
    nursing: PropTypes.string,
    nursingBreakSeconds: PropTypes.number,
  }),
  isEditable: PropTypes.bool,
  attendanceDetails: PropTypes.shape({
    schedule: PropTypes.shape({
      startTime: PropTypes.string,
      endTime: PropTypes.string,
      timezone: PropTypes.string,
    }),
  }),
  employeeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onUpdateSuccess: PropTypes.func,
};

export default TimesheetDetailModal;
